// src/components/BatchClassification.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';

export default function BatchClassification() {
  const toast = useToast();
  const [landings, setLandings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [athletes, setAthletes] = useState([]);
  const [events, setEvents] = useState([]);
  
  // Filters
  const [selectedAthlete, setSelectedAthlete] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [limit, setLimit] = useState('50');

  // Batch classification state
  const [classifications, setClassifications] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token') || JSON.parse(localStorage.getItem('user') || 'null')?.token;
  const userRole = JSON.parse(localStorage.getItem('user') || 'null')?.role;

  useEffect(() => {
    if (userRole === 'coach' || userRole === 'admin') {
      loadAthletes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  const loadAthletes = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/athletes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAthletes(res.data || []);
    } catch (err) {
      console.error('Failed to load athletes:', err);
    }
  };

  const fetchUnlabeledLandings = useCallback(async () => {
    setLoading(true);
    setError('');
    setClassifications({});
    
    try {
      const params = { limit };
      if (selectedAthlete) params.athlete_id = selectedAthlete;
      if (selectedEvent) params.event = selectedEvent;

      const res = await axios.get(`${API_BASE_URL}/api/unlabeled-landings`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      setLandings(res.data.landings || []);
      
      // Extract unique events
      const uniqueEvents = [...new Set((res.data.landings || []).map(l => l.event).filter(Boolean))];
      setEvents(uniqueEvents);
    } catch (err) {
      console.error('Failed to fetch unlabeled landings:', err);
      setError('Failed to load unlabeled landings');
      setLandings([]);
    } finally {
      setLoading(false);
    }
  }, [selectedAthlete, selectedEvent, limit, token]);

  const handleApplyFilters = () => {
    fetchUnlabeledLandings();
  };

  const updateClassification = (landingId, field, value) => {
    setClassifications(prev => ({
      ...prev,
      [landingId]: {
        ...prev[landingId],
        [field]: value
      }
    }));
  };

  const getClassificationStatus = (landingId) => {
    const c = classifications[landingId];
    return c && c.landing_type && c.entry;
  };

  const handleBatchSubmit = async () => {
    const classified = Object.entries(classifications).filter(([, c]) => c.landing_type && c.entry);
    
    if (classified.length === 0) {
      toast.error('Please classify at least one landing.');
      return;
    }

    const confirmMessage = `Submit classifications for ${classified.length} landings?`;
    if (!window.confirm(confirmMessage)) return;

    setSubmitting(true);

    try {
      const batchData = classified.map(([landingId, c]) => ({
        landing_id: parseInt(landingId),
        landing_type: c.landing_type,
        entry: c.entry
      }));

      const res = await axios.post(
        `${API_BASE_URL}/api/batch-label-landings`,
        { landings: batchData },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${res.data.successCount} classifications saved!${res.data.errorCount ? ` ${res.data.errorCount} failed.` : ''}`);
      setClassifications({});
      await fetchUnlabeledLandings();
    } catch (err) {
      console.error('Batch submission failed:', err);
      toast.error(`Batch submission failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      window.open(
        `${API_BASE_URL}/api/export-classifications?labeled=false&limit=10000`,
        '_blank'
      );
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export classifications');
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1>🎥 Batch Classification</h1>
      <p style={{ color: '#666' }}>Classify multiple unlabeled landings efficiently</p>

      {/* Filters */}
      <div style={{
        background: '#f5f5f5',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '15px'
      }}>
        {(userRole === 'coach' || userRole === 'admin') && (
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Filter by Athlete:
            </label>
            <select
              value={selectedAthlete}
              onChange={(e) => setSelectedAthlete(e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            >
              <option value="">-- All Athletes --</option>
              {athletes.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Filter by Event:
          </label>
          <select
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
          >
            <option value="">-- All Events --</option>
            {events.map(e => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Limit:
          </label>
          <select
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
          >
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <button
            onClick={handleApplyFilters}
            style={{
              flex: 1,
              padding: '8px 16px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Load Videos
          </button>
          <button
            onClick={handleExportCSV}
            style={{
              flex: 1,
              padding: '8px 16px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee', color: '#c33', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          Loading unlabeled landings...
        </div>
      )}

      {!loading && landings.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          No unlabeled landings found.
        </div>
      )}

      {!loading && landings.length > 0 && (
        <div>
          <div style={{ background: '#e7f3ff', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <strong>Total: {landings.length}</strong> | 
              <strong style={{ marginLeft: '15px' }}>Classified: {Object.values(classifications).filter(c => c.landing_type && c.entry).length}</strong>
            </span>
            <button
              onClick={handleBatchSubmit}
              disabled={submitting || Object.values(classifications).filter(c => c.landing_type && c.entry).length === 0}
              style={{
                padding: '10px 20px',
                background: Object.values(classifications).filter(c => c.landing_type && c.entry).length === 0 ? '#ccc' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? 'Submitting...' : '✅ Submit Batch'}
            </button>
          </div>

          {/* Grid of landing cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '20px' }}>
            {landings.map((landing, idx) => {
              const isClassified = getClassificationStatus(landing.id);
              const classification = classifications[landing.id] || {};

              return (
                <div
                  key={landing.id}
                  style={{
                    border: isClassified ? '2px solid #28a745' : '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '15px',
                    background: isClassified ? '#f0fdf4' : '#fafafa',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {/* Video Thumbnail */}
                  {landing.file_name && (
                    <video
                      src={`${API_BASE_URL}/videos/${landing.file_name}`}
                      crossOrigin="anonymous"
                      style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '4px', marginBottom: '10px' }}
                      controls
                    />
                  )}

                  {/* AI Predictions */}
                  <div style={{ background: '#e7f3ff', padding: '10px', borderRadius: '4px', marginBottom: '10px', fontSize: '12px' }}>
                    <p style={{ margin: '5px 0' }}>
                      <strong>AI Predicted:</strong> {landing.landing_type} | {landing.entry || 'N/A'}
                    </p>
                    {landing.over_rotated && <p style={{ margin: '5px 0', color: '#ff9800' }}>⚠️ Over-rotated</p>}
                    {landing.short_landing && <p style={{ margin: '5px 0', color: '#ff9800' }}>⚠️ Short landing</p>}
                  </div>

                  {/* Classification Form */}
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
                      Landing Quality:
                    </label>
                    <div style={{ display: 'flex', gap: '5px', marginBottom: '10px', flexWrap: 'wrap' }}>
                      {['Stick', 'Hop/Step', 'Over-rotated', 'Short Landing'].map(opt => (
                        <button
                          key={opt}
                          onClick={() => updateClassification(landing.id, 'landing_type', opt)}
                          style={{
                            flex: 1,
                            minWidth: '80px',
                            padding: '6px',
                            fontSize: '12px',
                            background: classification.landing_type === opt ? '#3b82f6' : '#ddd',
                            color: classification.landing_type === opt ? 'white' : '#333',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Entry Type */}
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
                      Entry Type:
                    </label>
                    <div style={{ display: 'flex', gap: '5px', marginBottom: '10px', flexWrap: 'wrap' }}>
                      {['Direct', 'Piked', 'Layouts', 'Arabesque'].map(opt => (
                        <button
                          key={opt}
                          onClick={() => updateClassification(landing.id, 'entry', opt)}
                          style={{
                            flex: 1,
                            minWidth: '70px',
                            padding: '6px',
                            fontSize: '12px',
                            background: classification.entry === opt ? '#3b82f6' : '#ddd',
                            color: classification.entry === opt ? 'white' : '#333',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Status */}
                  <div style={{ textAlign: 'center', padding: '5px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', background: isClassified ? '#d4edda' : '#fff3cd', color: isClassified ? '#155724' : '#856404' }}>
                    {isClassified ? '✅ Ready' : '⏳ Pending'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
